import React from 'react';
import { Collapse, Checkbox } from 'antd';
import { Check, CircleCheck, Package } from 'lucide-react';
import { categoryList, productList, SectionCard, StatusTag } from '../../../common';

const getCategoryStats = (products, categoryName) => {
    const categoryProducts = productList?.filter((p) => p?.category === categoryName);
    const selectedCount = categoryProducts?.filter((p) => products?.includes(p.value)).length || 0;
    const totalCount = categoryProducts?.length || 0;
    return { selectedCount, totalCount };
};

/** One selectable product tile. */
const ProductTile = ({ product, selected, onToggle }) => (
    <button
        type="button"
        aria-pressed={selected}
        onClick={onToggle}
        className={`group relative flex h-full w-full cursor-pointer flex-col items-center rounded-lg border bg-white p-3 text-center transition-colors ${
            selected ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50/40' : 'border-surface-200 hover:border-surface-300'
        }`}>
        <div className="mb-2 flex h-24 w-full items-center justify-center">
            <img alt={product.name} src={product.images} className="max-h-full w-full object-contain" />
        </div>
        {selected && (
            <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-[12px] text-white">
                <Check />
            </span>
        )}
        <span className={`flex min-h-[32px] items-center text-xs ${selected ? 'font-medium text-surface-900' : 'text-surface-600'}`}>{product.name}</span>
    </button>
);

function SegmentList({ products, onToggleProduct, onToggleCategory }) {
    const items = categoryList?.map((cat) => {
        const categoryProducts = productList?.filter((p) => p?.category === cat?.name);
        const { selectedCount, totalCount } = getCategoryStats(products, cat.name);
        const allSelected = selectedCount === totalCount && totalCount > 0;

        return {
            key: cat.name,
            label: (
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Checkbox
                            checked={allSelected}
                            indeterminate={selectedCount > 0 && !allSelected}
                            onChange={(e) => {
                                e.stopPropagation();
                                onToggleCategory(cat.name);
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-sm font-semibold text-surface-900">{cat.value}</span>
                        <StatusTag tone={selectedCount > 0 ? 'success' : 'neutral'} className="tabular-nums">
                            {`${selectedCount}/${totalCount}`}
                        </StatusTag>
                    </div>
                    {allSelected && <CircleCheck className="text-[16px] text-success-fg" />}
                </div>
            ),
            children: (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
                    {categoryProducts?.map((product) => (
                        <ProductTile
                            key={product.value}
                            product={product}
                            selected={products?.includes(product.value)}
                            onToggle={() => onToggleProduct(product.value)}
                        />
                    ))}
                </div>
            )
        };
    });

    return (
        <SectionCard
            icon={<Package />}
            title="Selección de Productos por Categoría"
            description="Selecciona los productos que se auditarán en esta segmentación"
            extra={
                <StatusTag tone="success" className="tabular-nums">
                    {products?.length || 0} seleccionados
                </StatusTag>
            }>
            <Collapse defaultActiveKey={categoryList?.map((c) => c.name)} expandIconPosition="end" items={items} />
        </SectionCard>
    );
}

export default SegmentList;
